/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AccessTokenResponse } from '../../../managers/sso';
import {
  describeSsoError,
  type ResolvedSsoError,
} from '../../../utils/ssoErrors';
import { normalizeDialingCode } from '../../../utils/phoneUtils';
import type { OnOtpVerified, OtpFlow } from '../types';
import { useLoginByMobile } from '../../account';
import { useVerifyMobileLogin } from '../api';

export interface UseLogin2FAOtpFlowOptions {
  displayPhoneNumber?: string;
  displayDialingCode?: string;
  startTimer: () => void;
  onVerified?: OnOtpVerified;
}

export function useLogin2FAOtpFlow({
  displayPhoneNumber,
  displayDialingCode,
  startTimer,
  onVerified,
}: UseLogin2FAOtpFlowOptions): OtpFlow {
  const { t } = useTranslation('account');
  const [localError, setLocalError] = useState<ResolvedSsoError>();
  const {
    mutateAsync: verifyMobileLogin,
    isPending: isVerifyMobileLoginPending,
  } = useVerifyMobileLogin();
  const { mutate: loginByMobile, isPending: isLoginByMobilePending } =
    useLoginByMobile();

  const setRequestError = useCallback(
    (error: unknown, fallback: string) =>
      setLocalError(describeSsoError(error, t, fallback)),
    [t],
  );

  const submitOtp = useCallback(
    (otp: string) => {
      verifyMobileLogin({ otp })
        .then((data: AccessTokenResponse) => onVerified?.({ session: data }))
        .catch((error: unknown) =>
          setRequestError(error, t('verifyPhonePage.verificationFailed')),
        );
    },
    [onVerified, setRequestError, t, verifyMobileLogin],
  );

  const requestOtp = useCallback(() => {
    if (!displayPhoneNumber || !displayDialingCode) {
      setLocalError({ message: t('verifyPhonePage.missingPhoneNumber') });
      return;
    }
    loginByMobile(
      {
        dialingCode: normalizeDialingCode(displayDialingCode),
        mobileNumber: displayPhoneNumber.trim(),
      },
      {
        onSuccess: () => {
          setLocalError(undefined);
          startTimer();
        },
        onError: (error: unknown) => {
          setRequestError(error, t('verifyPhonePage.requestFailed'));
          startTimer();
        },
      },
    );
  }, [
    displayPhoneNumber,
    displayDialingCode,
    loginByMobile,
    startTimer,
    setRequestError,
    t,
  ]);

  const clearError = useCallback(() => setLocalError(undefined), []);

  return {
    submitOtp,
    requestOtp,
    errorMessage: localError?.message,
    errorCode: localError?.code,
    isLoading: isVerifyMobileLoginPending || isLoginByMobilePending,
    clearError,
  };
}
